import { useAuth0 } from "@auth0/auth0-react";
import { Navigate, useNavigate } from "react-router";
import { AppLayout } from "@/components/layout/AppLayout";
import { ContinueLesson } from "@/components/dashboard/ContinueLesson";
import logo from "@/assets/logo.svg";
import { Card } from "@/components/ui/card";
import { useState, useEffect, useContext } from "react";
import { CircleAlert, Loader2 } from "lucide-react";
import { Container } from "../components/layout/Container";
import { extractAuth0MetaData } from "../lib/client/extractAuth0MetaData";
import { getUserDataOnClient } from "../lib/client/getUserData";
import { useAddNewUser } from "../lib/client/addNewUser";
import { UserContext } from "../lib/client/state/UserProvider";
import { CHP_LESSON_CONFIG } from "../data/lesson-config";

export function Dashboard() {
  const userContext = useContext(UserContext);
  const { isAuthenticated, user } = useAuth0();
  const navigate = useNavigate();
  const [currentLesson, setCurrentLesson] = useState(0);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  const { userName, userEmail, isLegacyCHPUser } = extractAuth0MetaData(user);
  const { data: userData, isLoading } = getUserDataOnClient(userEmail, {
    enabled: !!userEmail,
  });
  const { mutate: addNewUser } = useAddNewUser();

  // Update userContext when userData is fetched
  useEffect(() => {
    if (userData) {
      userContext.setUser(userData);
      console.log(userData);
    }
  }, [userData, userContext]);

  useEffect(() => {
    // Only attempt to add a new user if we've confirmed there's no existing user data
    // and we're not currently loading
    if (user && !isLoading && userData === null) {
      // TODO: Update program to be dynamic when multiple programs exist
      addNewUser(
        {
          email: userEmail,
          name: userName,
          programs: ["chp"],
          isLegacyCHPUser,
        },
        {
          onSuccess: (user) => {
            userContext.setUser(user);
            console.log(user);
          },
          onError: (error) => {
            // TODO: Emit Error Metric
            console.error("Error adding user:", error);
            setError("Error adding user");
          },
        }
      );
    }
  }, [user, userData, isLoading, userEmail, userName, addNewUser]);

  if (error) {
    return (
      <Container center>
        <div className="rounded-md border px-4 py-3 shadow-xs max-w-lg">
          <p className="text-sm pb-2 font-bold">
            <CircleAlert
              className="me-3 -mt-0.5 inline-flex text-red-500"
              size={16}
              aria-hidden="true"
            />
            We Encountered an Error
          </p>
          <p className="text-sm text-neutral-700">
            Please contact us for assistance. You can send us an email at
            info@amosinstitute.com.
          </p>
        </div>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container center>
        <div className="text-2xl font-bold">
          <Loader2 className="w-10 h-10 animate-spin" />
        </div>
      </Container>
    );
  }

  const lessons =
    userContext.selectedProgram === "chp" ? CHP_LESSON_CONFIG : [];
  const lastLessonCompleted = userContext.user?.programs?.find(
    (p) => p.program === userContext.selectedProgram
  )?.lastLessonCompleted;

  const nextLesson =
    lastLessonCompleted === null
      ? lessons[0]
      : lessons[lastLessonCompleted + 1];

  const handleGoToLesson = () => {
    if (nextLesson) {
      navigate(
        `/lessons/${userContext.selectedProgram}/${nextLesson.lessonCode}`
      );
    } else {
      navigate("/lessons");
    }
  };

  return (
    <AppLayout>
      <Card className="w-full md:w-3/4 lg:w-1/2 xl:w-1/3 p-2 border-2 border-[#A966AA]">
        <ContinueLesson
          subtitle={nextLesson?.subtitle || "Continue Your Journey"}
          title={nextLesson?.title || "Review Any Lesson"}
          description={
            nextLesson?.blurb ||
            "Refresh yourself on any lesson of the Cognitive Health Program."
          }
          imageUrl={nextLesson?.image || logo}
          onGoToLesson={handleGoToLesson}
          ctaText={nextLesson ? "Go to Lesson" : "Review Lessons"}
        />
      </Card>
    </AppLayout>
  );
}

export default Dashboard;
